// The room between games: the game up next (a picture of it, Start) and its settings; on the right the
// seat list, what's happened in the room and the game list (friends suggest, the host picks). After a
// game it also shows the result and tonight's games in this room.
import React from 'react';
import { Link } from 'react-router-dom';
import { SITE_GAMES, ROOM_GAMES_ORDER, gameName, gameLetter, playersText, inDevelopment, roomArt } from '../site/games';
import SeatList, { seatOrder, Crown } from './SeatList';
import GameOptions, { rulesetName } from './GameOptions';
import CtfRoomOptions from './CtfRoomOptions';
import { leagueOfRoom, leagueSeats, leagueSideName } from './leagueRoom';
import type { RoomView } from '../../types/room';

export interface WaitingRoomActions {
  setGame: (gameId: string, seats?: number) => void;
  setConfig: (config: Record<string, unknown>) => void;
  setSeatConfig: (config: Record<string, unknown>) => void;
  suggest: (gameId: string | null, gameName?: string) => void;
  arrange: (order: string[], watching: string[]) => void;
  transferLeader: (id: string) => void;
  addBot: (name?: string) => void;
  removeParticipant: (id: string) => void;
  setSpectator: (spectate: boolean) => void;
  setPlaytestMode: (enabled: boolean) => void;
  setName: (name: string) => void;
  onStart: () => void;
  onLeave: () => void;
}

export function RoomHeader({ slug, myName, hostName, onHome, onRename }: { slug: string; myName: string; hostName?: string | null; onHome: () => void; onRename: (n: string) => void }) {
  const [copied, setCopied] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(myName);
  const copy = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/room/${slug}`).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  const save = () => { const n = draft.trim(); if (n) onRename(n); setEditing(false); };
  return (
    <header className="site-header">
      <button type="button" className="site-brand" title="Leave the room (asks first)" onClick={onHome}>1001 Game Nights</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 auto', minWidth: 0 }}>
        <span className="site-muted">Room</span>
        <code>{slug}</code>
        {hostName && <span className="site-muted">· hosted by {hostName}</span>}
      </div>
      <button type="button" className="site-btn site-btn-outline" style={{ borderWidth: 1 }} onClick={copy}>{copied ? 'Copied!' : 'Copy invite link'}</button>
      {editing ? (
        <form onSubmit={e => { e.preventDefault(); save(); }} style={{ display: 'flex', gap: 6 }}>
          <input aria-label="Your name" value={draft} maxLength={30} autoFocus onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Escape' && setEditing(false)} style={{ width: 140 }} />
          <button type="submit" className="site-btn">Save</button>
        </form>
      ) : (
        <button type="button" className="site-btn" style={{ background: 'var(--field)', borderColor: 'var(--panel-line)' }} aria-label={`Your name: ${myName}. Change it`} onClick={() => { setDraft(myName); setEditing(true); }}>{myName}</button>
      )}
    </header>
  );
}

export default function WaitingRoom({ room, myId, isLeader, minPlayers, log, actions, leagueProblem }: {
  room: RoomView; myId: string; isLeader: boolean; minPlayers: number; log: string[]; actions: WaitingRoomActions; leagueProblem?: string | null;
}) {
  const me = room.participants.find(p => p.id === myId);
  const host = room.participants.find(p => p.isLeader);
  const game = SITE_GAMES.find(g => g.id === room.gameId);
  const name = gameName(room.gameId);
  const isCtf = room.gameId === 'capture-the-flag';
  const ctfDemo = isCtf && !!(room.config as { demo?: boolean }).demo;
  const { list, line } = seatOrder(room);
  const watchers = list.slice(line);
  const league = leagueOfRoom(room);
  const lseats = league ? leagueSeats(room, league) : null;
  const leagueMissing = league ? [!lseats?.blue && league.blue.name, !lseats?.red && league.red.name].filter(Boolean) as string[] : [];
  const leagueChoosing = league ? [league.blue, league.red].filter(s => !s.bot && s.pending > 0).map(s => s.name) : [];
  // A league game is played once: after a finished game (or a void) the room only leads back to the league.
  // The league may already have it (or plays it itself, when both sides are bots).
  const leagueBots = !!league && league.blue.bot && league.red.bot;
  const leagueOver = !!league && (!!room.leagueNote || !!league.settled || leagueBots || room.history.some(h => h.winner));
  const canStart = league ? leagueMissing.length === 0 && leagueChoosing.length === 0 && !leagueOver : room.playtestMode || ctfDemo || line >= minPlayers;
  const last = room.history[room.history.length - 1];
  const [hideResult, setHideResult] = React.useState<number | null>(null);
  const showResult = last && hideResult !== last.at;

  const suggesters = (id: string) => Object.entries(room.suggestions).filter(([, g]) => g === id)
    .map(([pid]) => room.participants.find(p => p.id === pid)?.name).filter(Boolean) as string[];
  const pick = (id: string) => {
    const g = SITE_GAMES.find(x => x.id === id);
    if (isLeader) { if (id !== room.gameId) actions.setGame(id, g?.maxPlayers); }
    else actions.suggest(room.suggestions[myId] === id ? null : id, g?.name);
  };

  const startNote = league
    ? (leagueMissing.length ? `Waiting for ${leagueMissing.join(' and ')} to open this game from the league page.`
      : leagueChoosing.length ? `Waiting for ${leagueChoosing.join(' and ')} to choose an improvement on the league page.`
      : `${league.blue.name} plays Blue, ${league.red.name} plays Red.`)
    : !canStart
    ? `${name} needs at least ${minPlayers} players: invite someone with the link, or add a bot.`
    : [
        room.seats != null && line >= room.seats ? (room.seats === 2 ? 'Both seats are filled.' : 'Every seat is filled.') : `${line} playing.`,
        watchers.length ? `${watchers.map(w => w.name).join(', ')} will watch.` : '',
      ].join(' ').trim();
  const seatName = (i: number, p: { name: string; type: string }) =>
    league ? leagueSideName(league, p as never) : isCtf ? (['Blue', 'Red'][i] ?? `Seat ${i + 1}`) : `Seat ${i + 1}`;
  const ruleset = rulesetName(room);

  const art = roomArt(room.gameId);
  const seatWord = room.seats == null ? null : room.seats === 2 ? 'two' : String(room.seats);
  const startBlock = leagueOver ? (
    <p style={{ padding: '14px 16px', borderRadius: 10, background: 'var(--bg)', textAlign: 'center' }}>
      {leagueBots && !league!.settled ? 'Two bots play this league game: the league plays it by itself, and its replay is on the league page.'
        : <>This league game is over{room.leagueNote ? `: ${room.leagueNote}` : league!.settled ? `: ${league!.settled}.` : '.'}</>} <Link to={`/league/${league!.leagueId}`}>Back to the league</Link>
    </p>
  ) : isLeader || (league && me?.slot != null && me.type !== 'bot') ? (
    <>
      <button type="button" className="site-btn site-btn-primary site-btn-big" style={{ fontSize: 24, padding: '6px 16px' }} onClick={actions.onStart} disabled={!canStart}>Start</button>
      <p className="site-muted" style={{ textAlign: 'center', fontSize: 14 }}>{startNote}</p>
    </>
  ) : league && me && leagueChoosing.some(n => n.toLowerCase() === me.name.toLowerCase()) ? (
    <p style={{ padding: '14px 16px', borderRadius: 10, background: 'var(--bg)', textAlign: 'center', color: '#f2d48f' }}>
      Choose your improvement first: the game can't start until you do. <Link to={`/league/${league.leagueId}`}>Go to the league page</Link>
    </p>
  ) : (
    <p style={{ padding: '14px 16px', borderRadius: 10, background: 'var(--bg)', textAlign: 'center', color: '#e8dfd2' }}>
      {league && leagueMissing.length ? `Waiting for ${leagueMissing.join(' and ')} to open this game from the league page.`
        : league && leagueChoosing.length ? `Waiting for ${leagueChoosing.join(' and ')} to choose an improvement.`
        : <>{me?.slot != null ? <>You play <strong>{seatName(me.slot, me)}</strong>. </> : ''}Waiting for {host?.name ?? 'the host'} to start.</>}
      {me?.slot === null ? ' You are watching this one.' : ''}
    </p>
  );
  const options = isCtf
    ? <CtfRoomOptions room={room} myId={myId} isLeader={isLeader} setConfig={actions.setConfig} setSeatConfig={actions.setSeatConfig} />
    : isLeader ? <GameOptions room={room} setConfig={actions.setConfig} /> : null;

  return (
    <main style={{ maxWidth: 1240, margin: '0 auto', padding: 24, display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
      <section aria-labelledby="next" style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {showResult && (
          <section aria-labelledby="result" className="site-panel" style={{ borderRadius: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px 28px' }}>
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <span className="site-kicker">Last game · {gameName(last.gameId)}</span>
              <h2 id="result" className="site-display" style={{ fontSize: 40, margin: '2px 0 4px', textTransform: 'none', letterSpacing: '.02em' }}>
                {last.winner === 'Draw' ? 'A draw' : last.winner ? <><span style={{ color: 'var(--accent)' }}>{last.winner}</span> wins</> : 'Ended early'}
              </h2>
              {last.how && <p style={{ color: '#e8dfd2' }}>{last.how}</p>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {league && <Link className="site-btn site-btn-primary site-btn-big" style={{ minHeight: 52, fontSize: 22 }} to={`/league/${league.leagueId}`}>Back to the league</Link>}
              {isLeader && !league && last.gameId === room.gameId && <button type="button" className="site-btn site-btn-primary site-btn-big" style={{ minHeight: 52, fontSize: 22 }} onClick={actions.onStart} disabled={!canStart}>Rematch</button>}
              {!league && <button type="button" className="site-btn" style={{ minHeight: 52 }} onClick={() => setHideResult(last.at)}>{isLeader ? 'Pick another game' : 'Close'}</button>}
            </div>
          </section>
        )}

 {leagueProblem && <p role="alert" style={{ padding: '10px 14px', borderRadius: 10, background: '#4a2a26', color: '#ffb3a8' }}>{leagueProblem}</p>}
        {league && (
          <section className="site-panel" style={{ background: 'linear-gradient(135deg, #3d3628, #34363f)', borderColor: '#6b5a3a', display: 'grid', gap: 6 }}>
            <span className="site-kicker" style={{ color: 'var(--accent)' }}>League game · {league.leagueName} · round {league.round}</span>
            <h2 className="site-display" style={{ fontSize: 32, textTransform: 'none' }}>
              <span style={{ color: 'var(--blue)' }}>{league.blue.name}</span> <span className="site-muted">vs</span> <span style={{ color: 'var(--red)' }}>{league.red.name}</span>
            </h2>
            <p className="site-muted">The armies come from the league, and the result goes back to it when the game ends. <Link to={`/league/${league.leagueId}`}>Back to the league</Link></p>
          </section>
        )}
        <article className="site-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {art
            ? <img src={art.src} alt={art.alt} style={{ display: 'block', width: '100%', height: 280, objectFit: 'cover', objectPosition: 'center 40%' }} />
            : (
              <div aria-hidden="true" style={{ height: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'var(--sunk)' }}>
                <span className="site-letter" style={{ width: 88, height: 88, fontSize: 52, borderRadius: 16, background: 'var(--bg)' }}>{game ? gameLetter(game) : '?'}</span>
                <span className="site-muted site-small">A picture of {name} in progress is on its way</span>
              </div>
            )}
          <div style={{ padding: '20px 24px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 24px' }}>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              <span className="site-kicker" style={{ color: 'var(--accent-soft)' }}>Up next</span>
              <h2 id="next" className="site-display" style={{ fontSize: 40, lineHeight: 1.1, margin: '2px 0', textTransform: 'none', letterSpacing: '.02em' }}>{name}</h2>
              <span className="site-muted">
                {game && playersText(game)}{!isLeader && ruleset ? ` · ${ruleset}` : ''}{game && inDevelopment(game) ? ' · in development' : ''} · <Link to={`/games/${room.gameId}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>How to play ↗</Link>
              </span>
            </div>
            <div style={{ flex: '0 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
              {startBlock}
            </div>
          </div>
        </article>

        {(options || (isLeader && !league)) && (
          <article aria-labelledby="settings" className="site-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h3 id="settings" className="site-display" style={{ fontSize: 24 }}>Settings</h3>
            {options}
            {isLeader && !league && (
              <label className="site-muted" style={{ fontSize: 14 }}>
                <input type="checkbox" checked={room.playtestMode} onChange={e => actions.setPlaytestMode(e.target.checked)} />{' '}
                Playtest mode: start with fewer players than the game needs
              </label>
            )}
          </article>
        )}

        {room.history.length > 0 && (
          <section aria-labelledby="tonight" className="site-panel">
            <h3 id="tonight" className="site-display" style={{ fontSize: 24, marginBottom: 12 }}>Tonight in this room</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: 13 }}>
                  <th scope="col" style={{ padding: '6px 8px', fontWeight: 600 }}>Game</th>
                  <th scope="col" style={{ padding: '6px 8px', fontWeight: 600 }}>Winner</th>
                  <th scope="col" style={{ padding: '6px 8px', fontWeight: 600 }}>How</th>
                </tr>
              </thead>
              <tbody>
                {room.history.slice().reverse().map(r => (
                  <tr key={r.at} style={{ borderTop: '1px solid var(--panel-line)' }}>
                    <td style={{ padding: '10px 8px' }}>{gameName(r.gameId)}</td>
                    <td style={{ padding: '10px 8px' }}>{r.winner ?? '—'}</td>
                    <td style={{ padding: '10px 8px', color: 'var(--muted)' }}>{r.how ?? (r.winner ? '' : 'Ended early')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>

      <aside aria-label="People and games" style={{ flex: '1 1 360px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="site-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h2 className="site-display" style={{ fontSize: 24 }}>Seats</h2>
            {isLeader && !league ? <span className="site-muted" style={{ fontSize: 14 }}>Drag to rearrange</span>
              : !league && host && <span className="site-muted" style={{ fontSize: 14 }}>{host.name} arranges them</span>}
          </div>
          <SeatList room={room} myId={myId} isLeader={isLeader} seatName={seatName} locked={!!league}
            arrange={actions.arrange} removeParticipant={actions.removeParticipant} setSpectator={actions.setSpectator} transferLeader={actions.transferLeader} />
          <p className="site-muted site-small">
            {league
              ? "The league decides who plays: its Blue and Red. Anyone else in the room watches."
              : <>{seatWord ? `The first ${seatWord} seats play` : 'The seats above the line play'}; everyone below the line watches. The crown <Crown size={13} /> is the host, who runs the room, playing or watching.{isLeader ? ' Drag a name, or use the arrows, to move people; tap the crown by a name to hand hosting over.' : ''}</>}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isLeader && !league && <button type="button" className="site-btn site-btn-small" onClick={() => actions.addBot()} disabled={room.seats != null && line >= room.seats}>Add a bot</button>}
            <button type="button" className="site-btn site-btn-small" style={{ borderColor: 'transparent', color: 'var(--muted)' }} onClick={actions.onLeave}>Leave the room</button>
          </div>
        </div>

        {log.length > 0 && (
          <div className="site-panel" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
            <span className="site-kicker">In the room</span>
            {log.map((m, i) => <span key={i}>{m}</span>)}
          </div>
        )}

        {!league && (
          <section aria-labelledby="pick" className="site-panel" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <h2 id="pick" className="site-display" style={{ fontSize: 20 }}>{isLeader ? 'Choose a game' : 'Suggest a game'}</h2>
              <span className="site-muted" style={{ fontSize: 13 }}>{isLeader ? 'Friends can suggest one' : `${host?.name ?? 'The host'} sees it`}</span>
            </div>
            {!isLeader && room.suggestions[myId] && (
              <p role="status" style={{ color: 'var(--accent-soft)', fontSize: 14 }}>
                You suggested {SITE_GAMES.find(x => x.id === room.suggestions[myId])?.name ?? 'a game'}{room.suggestions[myId] === room.gameId ? ` and ${host?.name ?? 'the host'} picked it` : ''}; tap it again to take it back.
              </p>
            )}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {ROOM_GAMES_ORDER.map(g => {
                const on = g.id === room.gameId;
                const mine = room.suggestions[myId] === g.id;
                const who = suggesters(g.id);
                const note = who.length > 0
                  ? (mine ? (who.length > 1 ? `You and ${who.length - 1} more suggest this` : 'You suggested this') : `${who.join(', ')} suggest${who.length === 1 ? 's' : ''} this`)
                  : on && !isLeader ? 'Up next' : inDevelopment(g) ? 'In development' : 'Finished';
                const noteColor = who.length > 0 || on ? 'var(--accent-soft)' : inDevelopment(g) ? 'var(--warn)' : 'var(--muted)';
                return (
                  <li key={g.id}>
                    <button type="button" aria-pressed={isLeader ? on : mine} onClick={() => pick(g.id)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', minHeight: 44, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', color: 'var(--ink)', fontSize: 14,
                        background: on ? 'var(--accent-bg)' : 'transparent', border: `1px solid ${on ? 'var(--accent)' : mine ? 'var(--accent-soft)' : 'transparent'}` }}>
                      <span className="site-letter" aria-hidden="true" style={{ width: 28, height: 28, fontSize: 17, borderRadius: 6 }}>{gameLetter(g)}</span>
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                        <span style={{ fontWeight: 600 }}>{g.name}</span>
                        <span style={{ fontSize: 12, color: noteColor }}>{note}</span>
                      </span>
                      <span className="site-muted" style={{ fontSize: 12, flex: 'none' }}>{playersText(g)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </aside>
    </main>
  );
}
